import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreateComplianceReport, IReport, IChecklist } from '../../../../Components/Compliance/CreateReport';
import { SelectOption } from '../../../../../../Components/Form/Form.types';
import { ComplianceSessionInformation } from './SessionInformation';
import { AptorApi, useAptorApi } from '../../../../../../Api';
import { entityToSelectOption } from '../../../../../../Utilities';
import { useComplianceSession } from '../../../../../../Context/ComplianceSessionContext';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../../Context/UserContext/UserContext';
import { Grid } from '@material-ui/core';
import { BackButton } from '../../../../../../Components';
import { ModuleContext } from '../../../../../../Context/ModuleContext/ModuleContext';
import { useIntl } from 'react-intl';

export const CreateLawComplianceReport = ({info}:any) => {
  const params = useParams<{ id: string; planid: string }>();
  const lawid = parseInt(params.id!);
  const planid = parseInt(params.planid!);
  const { api, abortController } = useAptorApi();
  const navigate = useNavigate();
  const { hasAccessToFeature, id } = useContext(UserContext);
  const lightVersionPlanId = hasAccessToFeature(LawPortalFeature.SettingsForBasisOfEvaluationControl, AccessLevel.Read)
    ? planid
    : undefined;
  const [loadingChecklist, setLoadingChecklist] = useState<boolean>(true);

  const [loaded, setLoaded] = useState<boolean>(false);
  const [companyUnits, setCompanyUnits] = useState<SelectOption[]>([]);
  const [companyUnitId, setCompanyUnitId] = useState<number>();

  const [checklist, setChecklist] = useState<IChecklist>();
  const { complianceSession, clearComplianceSession, completeLawCompliance } = useComplianceSession();
  const isBackNavigation = useRef(false);
  const [LawComplianceData, setLawsComplianceData] = useState("");
  const path = window.location.pathname.split("/");
    const { setHeader } = useContext(ModuleContext);
    const { formatMessage } = useIntl();

  useEffect(()=>{
      setHeader(
      formatMessage({ id: 'law-portal.legal.compliance.header.with.name', defaultMessage: 'Evaluation control' },{ category:info.category,no: info.number, lawName: info.name }),
    );
  },[info])
  useEffect(() => {
    const checkEdit = path[path.length - 1];
    if (checkEdit === "edit") {
      if (path?.length && path[5] === "other-requirements") {
        api.getLawPortalRequirementChecklistWithData(planid, lawid).then((chkl: any) => {
          if (!abortController.current.signal.aborted) {
            setLawsComplianceData(chkl);
            setChecklist(chkl);
            setLoadingChecklist(false);
          }
        });
      } else {
        api.getLawPortalLawChecklistWithData(planid, lawid).then((chkl: any) => {
          if (!abortController.current.signal.aborted) {
            setLawsComplianceData(chkl);
            setChecklist(chkl);
            setLoadingChecklist(false);
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    if (companyUnitId !== undefined) {
      if (path[path.length - 1] === "edit") {
        return;
      }

      setLoadingChecklist(true);
      if (path?.length && path[5] === "other-requirements") {
        api.getLawPortalRequirementChecklist<IChecklist>(companyUnitId, lawid, lightVersionPlanId).then((chkl) => {
          if (!abortController.current.signal.aborted) {
            setChecklist(chkl);
            setLoadingChecklist(false);
          }
        });
      } else {
        api.getLawPortalLawChecklist<IChecklist>(lawid, companyUnitId, lightVersionPlanId).then((chkl) => {
          if (!abortController.current.signal.aborted) {
            setChecklist(chkl);
            setLoadingChecklist(false);
          }
        });
      }
    } else {
      if (path?.length && path[5] === "other-requirements") {
        api.getUserCompanyUnitsForRequirementCompliance(lawid).then((userCompanyUnits) => {
          if (!abortController.current.signal.aborted) {
            setCompanyUnits(userCompanyUnits.items.map(entityToSelectOption));
            setLoaded(true);
          }
        });
      } else {
        api.getUserCompanyUnitsForLawCompliance(lawid).then((userCompanyUnits) => {
          if (!abortController.current.signal.aborted) {
            setCompanyUnits(userCompanyUnits.items.map(entityToSelectOption));
            setLoaded(true);
          }
        });
      }
    }
  }, [api, abortController, companyUnitId, lawid, lightVersionPlanId]);

  const submitAction = (unitId: number, reportId: any, a: AptorApi, report: IReport) => {
    const path = window.location.pathname?.split("/");
    const checkEdit = path[path.length - 1];
    if (checkEdit === "edit") {
      if (path?.length && path[5] === "other-requirements") {
        return a.updateLawPortalRequirementComplianceReport(lawid, unitId, Number(reportId), report);
      } else {
        return a.updateLawPortalLawComplianceReport(lawid, unitId, Number(reportId), report);
      }
    } else {
      if (path?.length && path[5] === "other-requirements") {
        return a.createLawPortalRequirementComplianceReport(lawid, unitId,{...report,PlanId:planid});
      } else {
        return a.createLawPortalLawComplianceReport(lawid, unitId, {...report,PlanId:planid});
      }
    }
  };

  const onCreateSuccess = () => {
    if (complianceSession) {
      completeLawCompliance(lawid);
      const nextLaw = complianceSession.next();
      if (nextLaw) {
        navigate(nextLaw.complianceUrl);
      } else {
        navigate('/law-portal/legal-compliance-control/carry');
      }
    } else {
      navigate(`/law-portal/legal-compliance-control/carry`);
    }
  };

  const handleBack = () => {
    isBackNavigation.current = true;
    clearComplianceSession();
    navigate('/law-portal/legal-compliance-control/carry');
  };

  const handleFetchComplianceUsers = useCallback(
    async (companyUnitId: number) => {
      if (path?.length && path[5] === "other-requirements") {
        const result = await api.getParticipantsForRequirementCompliance(lawid, companyUnitId);
        return result.items.map(entityToSelectOption);
      } else {
        const result = await api.getParticipantsForLawCompliance(lawid, companyUnitId);
        return result.items.map(entityToSelectOption);
      }
    },
    [api, id],
  );

  const { firstName, lastName } = useContext(UserContext);
  const checkedDirectPlan = useRef(false);

  useEffect(() => {
    if (checkedDirectPlan.current) {
      return;
    }
    checkedDirectPlan.current = true;

    const path = window.location.pathname?.split("/");
    const checkEdit = path[path.length - 1];
    if (checkEdit !== "edit") {
      const localcomplianceSession: any = localStorage.getItem(`ComplianceSession_plan_${planid}_userID_${id}`);
      const localcompliancesessiondata = JSON.parse(localcomplianceSession);
      const currentuserName = `${firstName ?? ''} ${lastName ?? ''}`?.toLowerCase();
      if (localcompliancesessiondata?.done?.length === 0) {
        if (complianceSession) {
          api.getdirectPlanLaws(planid).then(async (res) => {
            if (isBackNavigation.current) {
              return;
            }
            if (res.laws?.length) {
              await res?.laws?.forEach((item: any) => {
                if (isBackNavigation.current) {
                  return;
                }
                if (item?.isStarted && currentuserName === `${item.responsible ?? ''}`?.toLowerCase()) {
                  completeLawCompliance(item.id);
                }
                const nextLaw = complianceSession.next();
                if (nextLaw) {
                  navigate(nextLaw.complianceUrl);
                } else {
                  navigate('/law-portal/legal-compliance-control/carry');
                }
              });
            } else if (res.requirements?.length) {
              await res?.requirements?.forEach((item: any) => {
                if (isBackNavigation.current) {
                  return;
                }
                if (item?.isStarted && currentuserName === `${item.responsible ?? ''}`?.toLowerCase()) {
                  completeLawCompliance(item.id);
                }
                const nextLaw = complianceSession.next();
                if (nextLaw) {
                  navigate(nextLaw.complianceUrl);
                } else {
                  navigate('/law-portal/legal-compliance-control/carry');
                }
              });
            }
          }).catch(() => undefined)
          // const nextLaw = complianceSession.next();
          // if (nextLaw) {
          //   history.push(nextLaw.complianceUrl);
          // } else {
          //   history.push('/law-portal/legal-compliance-control/carry');
          // }
        } else {
          navigate(`/law-portal/legal-compliance-control/carry`);
        }
      }
    }
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <BackButton disableMargin link="" onclick={handleBack} />
      </Grid>
      <Grid item xs={12}>
        <ComplianceSessionInformation id={lawid} info={info} />
        <Grid container direction="column" spacing={2}>
          <Grid item style={{ marginTop: 20 }}>
            <CreateComplianceReport
              key={path?.length && path[5] === "other-requirements" ? `requirement-${lawid}` : `law-${lawid}`}
              lawId={path?.length && path[5] === "other-requirements" ? undefined : lawid}
              requirementId={path?.length && path[5] === "other-requirements" ? lawid : undefined}
              titleKey={path?.length && path[5] === "other-requirements" ? "law-portal.requirements.compliance.heading" : "law-portal.our-laws.compliance.heading"}
              summaryKey={path?.length && path[5] === "other-requirements" ? "law-portal.requirements.compliance.summary.label" : "law-portal.our-laws.compliance.summary"}
              storageKey={`compliance-state-for-plan-${planid}-${path?.length && path[5] === "other-requirements" ? "requirement" : "law"}-${lawid}`}
              loaded={loaded}
              loadingChecklist={loadingChecklist}
              units={companyUnits}
              fetchComplianceUsers={handleFetchComplianceUsers}
              onCompanyUnitChange={setCompanyUnitId}
              checklist={checklist}
              disableHide={!hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage)}
              submitAction={submitAction}
              onCreateSuccess={onCreateSuccess}
              LawComplianceData={LawComplianceData}
              setLoaded={setLoaded}
              info={info}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};